import { useCallback, useRef } from 'react';

export const useSuccessSound = () => {
  const audioContextRef = useRef<AudioContext | null>(null);

  const playSuccessSound = useCallback(() => {
    try {
      // Create or reuse AudioContext
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      
      // Resume context if suspended (browser autoplay policy)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      const now = ctx.currentTime;
      
      // Create a pleasant celebratory chime sequence
      const notes = [
        { freq: 523.25, start: 0, duration: 0.15 },      // C5
        { freq: 659.25, start: 0.1, duration: 0.15 },    // E5
        { freq: 783.99, start: 0.2, duration: 0.2 },     // G5
        { freq: 1046.50, start: 0.35, duration: 0.4 },   // C6 (hold longer)
      ];

      notes.forEach(({ freq, start, duration }) => {
        // Oscillator for the tone
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        // Use sine wave for a soft, pleasant sound
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(freq, now + start);
        
        // Add a subtle harmonic for richness
        const harmonic = ctx.createOscillator();
        const harmonicGain = ctx.createGain();
        harmonic.type = 'sine';
        harmonic.frequency.setValueAtTime(freq * 2, now + start);
        harmonicGain.gain.setValueAtTime(0.15, now + start);
        
        // Envelope for smooth attack and release
        gainNode.gain.setValueAtTime(0, now + start);
        gainNode.gain.linearRampToValueAtTime(0.12, now + start + 0.02); // Quick attack
        gainNode.gain.exponentialRampToValueAtTime(0.001, now + start + duration); // Smooth decay
        
        harmonicGain.gain.setValueAtTime(0, now + start);
        harmonicGain.gain.linearRampToValueAtTime(0.03, now + start + 0.02);
        harmonicGain.gain.exponentialRampToValueAtTime(0.001, now + start + duration);
        
        // Connect nodes
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        harmonic.connect(harmonicGain);
        harmonicGain.connect(ctx.destination);
        
        // Play
        oscillator.start(now + start);
        oscillator.stop(now + start + duration + 0.1);
        
        harmonic.start(now + start);
        harmonic.stop(now + start + duration + 0.1);
      });

      // Add a subtle sparkle effect
      const sparkleCount = 5;
      for (let i = 0; i < sparkleCount; i++) {
        const sparkle = ctx.createOscillator();
        const sparkleGain = ctx.createGain();
        
        sparkle.type = 'sine';
        const sparkleFreq = 2000 + Math.random() * 2000;
        const sparkleStart = 0.3 + Math.random() * 0.3;
        
        sparkle.frequency.setValueAtTime(sparkleFreq, now + sparkleStart);
        sparkle.frequency.exponentialRampToValueAtTime(sparkleFreq * 1.5, now + sparkleStart + 0.1);
        
        sparkleGain.gain.setValueAtTime(0, now + sparkleStart);
        sparkleGain.gain.linearRampToValueAtTime(0.02, now + sparkleStart + 0.01);
        sparkleGain.gain.exponentialRampToValueAtTime(0.001, now + sparkleStart + 0.15);
        
        sparkle.connect(sparkleGain);
        sparkleGain.connect(ctx.destination);
        
        sparkle.start(now + sparkleStart);
        sparkle.stop(now + sparkleStart + 0.2);
      }
    } catch (error) {
      // Silently fail - audio is enhancement, not critical
      console.log('Audio playback not available');
    }
  }, []);

  return { playSuccessSound };
};
